import { authFetch } from '@/services/apiClient'
import type { InvoiceInfo, BillingInfo } from '@/types/invoiceInfo'
import { toBillingInfoPayload } from '@/types/invoiceInfo'

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  message?: string
}

// ผู้ได้ที่นั่ง/คอร์สจากรายการสั่งซื้อ — Onsite: รายชื่อผู้เข้าอบรม, ออนไลน์ที่บริษัทซื้อให้พนักงาน: อีเมลที่ได้คอร์ส
export interface OrderParticipant {
  first_name?: string
  last_name?: string
  email: string
  phone?: string | null
  has_account: boolean
  is_self?: boolean
}

export interface OrderItem {
  order_item_id: number
  item_type: 'COURSE' | 'EMPLOYER_PLAN' | 'BOOK'
  ref_id: number
  item_name: string
  unit_price: number
  quantity: number
  subtotal: number
  batch_id: number | null
  participants: OrderParticipant[]
}

// รายชื่อที่ส่งมากับคำขอจ่ายเงิน — ซื้อออนไลน์ให้พนักงานใช้แค่ email, จอง Onsite (บริษัท) ใช้ครบทุกช่อง
export interface ParticipantInput {
  email: string
  first_name?: string
  last_name?: string
  phone?: string
}

export type RecipientStatus = 'OK' | 'NOT_REGISTERED' | 'ALREADY_ENROLLED' | 'DUPLICATE' | 'INVALID_EMAIL'

export interface RecipientCheck {
  email: string
  status: RecipientStatus
  display_name: string
}

export interface Payment {
  payment_id: number
  payment_method: 'ONLINE' | 'FREE'
  external_ref: string | null
  amount: number
  status: string
  paid_at: string | null
}

// ที่อยู่/สถานะจัดส่งของคำสั่งซื้อหนังสือ (ส่งฟรีทุกรายการ → shipping_fee = 0)
export type ShippingStatus = 'PENDING' | 'PREPARING' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED'

export interface OrderShipping {
  recipient_name: string
  phone: string
  address: string
  province: string
  district: string
  subdistrict: string
  postal_code: string
  shipping_fee: number
  shipping_status: ShippingStatus
  carrier: string | null
  tracking_number: string | null
  shipped_at: string | null
  delivered_at: string | null
}

// ที่อยู่จัดส่งที่ส่งไปกับคำขอจ่ายเงิน (บังคับเมื่อสั่งซื้อหนังสือ)
export interface ShippingInput {
  recipient_name: string
  phone: string
  address: string
  province: string
  district: string
  subdistrict: string
  postal_code: string
}

export interface Order {
  order_id: number
  order_number: string
  customer_id: number
  status: 'PENDING' | 'PAID' | 'FAILED' | 'REFUNDED'
  total_amount: number
  discount: number
  final_amount: number
  coupon_code: string | null
  paid_at: string | null
  created_date: string
  items: OrderItem[]
  payment: Payment | null
  billing_info: BillingInfo | null   // ข้อมูลผู้รับใบเสร็จ/ใบกำกับภาษี — null ถ้าไม่ได้ขอตอนจ่ายเงิน
  shipping: OrderShipping | null     // เฉพาะคำสั่งซื้อหนังสือ
  // เฉพาะผลของ payOrder แบบจ่ายออนไลน์: order ยังเป็น PENDING — ต้องพาลูกค้าไป redirect_url (หน้าของ TEP) แล้วรอผลที่ /payment/result/:orderId
  payment_pending?: boolean
  redirect_url?: string
}

// ผลถามสถานะการชำระเงิน — GET /api/orders/:id/payment-status
export interface PaymentStatus {
  order_id: number
  status: 'PENDING' | 'PAID' | 'FAILED' | 'REFUNDED'
  order_number: string | null
  needs_review: boolean   // จ่ายเงินแล้วแต่ส่งมอบอัตโนมัติไม่ได้ — ทีมงานกำลังตรวจสอบ
  expired: boolean        // ลิงก์จ่ายเงินหมดอายุแล้วและยังไม่จ่าย
}

async function parseJson<T>(res: Response): Promise<ApiResponse<T>> {
  try {
    return await res.json()
  } catch {
    return { success: false, error: 'เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ' }
  }
}

// ผลตรวจคูปอง (ส่วนลดที่ระบบคำนวณเองจากราคาในฐานข้อมูล) — POST /api/coupons/validate
export interface CouponQuote {
  valid: boolean
  code: string
  discount_type: 'PERCENT' | 'FIXED'
  subtotal: number
  discount: number
  final_amount: number
}

export const validateCoupon = async (params: {
  code: string
  itemType: 'COURSE' | 'BOOK'
  refId: number
  quantity?: number
  batchId?: number | null
}): Promise<CouponQuote> => {
  const res = await authFetch('/coupons/validate', {
    method: 'POST',
    body: JSON.stringify({
      code: params.code,
      item_type: params.itemType,
      ref_id: params.refId,
      quantity: params.quantity ?? 1,
      batch_id: params.batchId ?? undefined,
    }),
  })
  const json = await parseJson<CouponQuote>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'ตรวจสอบคูปองไม่สำเร็จ')
  return json.data
}

export const createOrder = async (
  itemType: 'COURSE' | 'EMPLOYER_PLAN' | 'BOOK',
  refId: number,
  quantity = 1,
  batchId?: number | null,
  couponCode?: string | null
): Promise<Order> => {
  const res = await authFetch('/orders', {
    method: 'POST',
    body: JSON.stringify({
      item_type: itemType, ref_id: refId, quantity, batch_id: batchId ?? undefined,
      coupon_code: couponCode || undefined,
    }),
  })
  const json = await parseJson<Order>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'สร้างคำสั่งซื้อไม่สำเร็จ')
  return json.data
}

// สร้างคำสั่งซื้อจากตะกร้า (คอร์สออนไลน์ หรือ หนังสือ ชนิดเดียวกัน) — ราคาคำนวณที่ server ใหม่เสมอ
export const createCartOrder = async (
  items: { item_type: string; ref_id: number; quantity: number }[],
  couponCode?: string | null
): Promise<Order> => {
  const res = await authFetch('/orders', {
    method: 'POST',
    body: JSON.stringify({ items, coupon_code: couponCode || undefined }),
  })
  const json = await parseJson<Order>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'สร้างคำสั่งซื้อไม่สำเร็จ')
  return json.data
}

export const payOrder = async (
  orderId: number,
  paymentMethod: 'ONLINE' | 'FREE',
  participants?: ParticipantInput[] | Record<string, ParticipantInput[]>,   // list = รายการเดียว · object {order_item_id: [...]} = ตะกร้าหลายรายการ
  invoiceInfo?: InvoiceInfo,
  shipping?: ShippingInput
): Promise<Order> => {
  // รุ่นอบรมอ่านจากคำสั่งซื้อฝั่ง backend เอง — ไม่ส่ง batch_id ตอนจ่ายอีกแล้ว
  const res = await authFetch(`/orders/${orderId}/pay`, {
    method: 'POST',
    body: JSON.stringify({
      payment_method: paymentMethod,
      participants,
      billing_info: invoiceInfo ? toBillingInfoPayload(invoiceInfo) : undefined,
      shipping,
    }),
  })
  const json = await parseJson<Order>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'ชำระเงินไม่สำเร็จ')
  return json.data
}

// ถามสถานะการชำระเงินของคำสั่งซื้อ (หลังกลับจากหน้าของ TEP) — backend ถาม Gateway ให้เองถ้า webhook ยังไม่มา
export const getPaymentStatus = async (orderId: number): Promise<PaymentStatus> => {
  const res = await authFetch(`/orders/${orderId}/payment-status`)
  const json = await parseJson<PaymentStatus>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'ตรวจสอบสถานะการชำระเงินไม่สำเร็จ')
  return json.data
}

// ลูกค้ายืนยันว่าได้รับหนังสือแล้ว (สถานะจัดส่ง SHIPPED → DELIVERED)
export const confirmOrderReceived = async (orderId: number): Promise<Order> => {
  const res = await authFetch(`/orders/${orderId}/confirm-received`, { method: 'POST' })
  const json = await parseJson<Order>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'ยืนยันการรับสินค้าไม่สำเร็จ')
  return json.data
}

export const getMyOrders = async (): Promise<Order[]> => {
  const res = await authFetch('/my-orders')
  const json = await parseJson<Order[]>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'โหลดประวัติคำสั่งซื้อไม่สำเร็จ')
  return json.data
}

// บริษัทตรวจอีเมลพนักงานล่วงหน้าก่อนซื้อคอร์สออนไลน์ให้ (เฉพาะบัญชีบริษัท)
// batchId: Onsite — สมัครได้ไม่จำกัดครั้ง (กันที่ชื่อ-นามสกุลซ้ำ) จึงไม่คืน ALREADY_ENROLLED · ไม่ส่ง = ตรวจระดับคอร์สออนไลน์
export const checkRecipients = async (courseId: number, emails: string[], batchId?: number | null): Promise<RecipientCheck[]> => {
  const res = await authFetch('/orders/check-recipients', {
    method: 'POST',
    body: JSON.stringify({ course_id: courseId, emails, batch_id: batchId ?? undefined }),
  })
  const json = await parseJson<RecipientCheck[]>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'ตรวจสอบอีเมลไม่สำเร็จ')
  return json.data
}

// ยกเลิกคำสั่งซื้อที่ยังรอชำระของตัวเอง — ปล่อยที่นั่งที่กันไว้ทันที (best-effort ไม่ throw)
export const cancelOrder = async (orderId: number): Promise<void> => {
  try {
    await authFetch(`/orders/${orderId}`, { method: 'DELETE' })
  } catch {
    /* คำสั่งซื้อจะหมดอายุเองใน 10 นาที */
  }
}
